import type { WorldMap } from '../api/types';

/**
 * Always-on circular minimap (top-left): districts, roads and a heading arrow
 * that tracks the player live. Clicking it opens the full travel map. Built
 * once from WorldMap data as inline SVG — same source of truth as everything
 * else (DR-05).
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

export class Minimap {
  private readonly marker: SVGGElement;

  constructor(map: WorldMap, onClick: () => void) {
    const host = document.createElement('div');
    host.id = 'minimap';
    host.addEventListener('click', onClick);
    document.getElementById('hud')!.appendChild(host);

    const pad = 10;
    const svg = document.createElementNS(SVG_NS, 'svg');
    const r = map.worldHalf + pad;
    svg.setAttribute('viewBox', `${-r} ${-r} ${r * 2} ${r * 2}`);

    for (const road of map.roads) {
      const line = document.createElementNS(SVG_NS, 'line');
      line.setAttribute('x1', String(road.from.x));
      line.setAttribute('y1', String(road.from.z));
      line.setAttribute('x2', String(road.to.x));
      line.setAttribute('y2', String(road.to.z));
      line.setAttribute('stroke', '#565b63');
      line.setAttribute('stroke-width', String(road.width));
      svg.appendChild(line);
    }
    for (const district of map.districts) {
      const circle = document.createElementNS(SVG_NS, 'circle');
      circle.setAttribute('cx', String(district.center.x));
      circle.setAttribute('cy', String(district.center.z));
      circle.setAttribute('r', String(district.radius * 0.55));
      circle.setAttribute('fill', district.color);
      circle.setAttribute('fill-opacity', '0.4');
      circle.setAttribute('stroke', 'rgba(255,255,255,0.35)');
      circle.setAttribute('stroke-width', '2');
      svg.appendChild(circle);
    }

    this.marker = document.createElementNS(SVG_NS, 'g');
    const arrow = document.createElementNS(SVG_NS, 'path');
    arrow.setAttribute('d', 'M 0 -16 L 11 12 L 0 5 L -11 12 Z');
    arrow.setAttribute('fill', '#ffd45e');
    arrow.setAttribute('stroke', 'rgba(0,0,0,0.55)');
    arrow.setAttribute('stroke-width', '2.5');
    this.marker.appendChild(arrow);
    svg.appendChild(this.marker);

    host.appendChild(svg);
  }

  /** `headingDeg` is the player/camera yaw θ (view forward = (−sinθ, −cosθ)). */
  update(x: number, z: number, headingDeg: number): void {
    const rad = (headingDeg * Math.PI) / 180;
    const rot = (Math.atan2(-Math.sin(rad), Math.cos(rad)) * 180) / Math.PI;
    this.marker.setAttribute('transform', `translate(${x} ${z}) rotate(${rot})`);
  }
}
